import { useState, useEffect } from 'react';
import { ScaffoldedCharacter } from '../types';
import { enhanceCharacter } from '../api';

/**
 * Whether an enhanced value is an actual suggestion worth showing or applying.
 *
 * The enhance endpoint emits explicit nulls (and empty arrays) for fields the
 * model left out, and `null !== undefined`, so the old checks treated every
 * one of those as a real suggestion — rendering "Empty" in the diff and, via
 * Accept All, overwriting good values with nothing.
 */
function isMeaningfulEnhancement(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

interface EditCharacterModalProps {
  character: ScaffoldedCharacter;
  isOpen: boolean;
  onClose: () => void;
  onSave: (character: ScaffoldedCharacter) => void;
  /** Original world concept (for AI enhancement context) */
  worldPrompt?: string;
  /** Scaffolded setting definition (preferred context for AI) */
  settingDefinition?: string;
  /** Rich context from guided world creation (for AI enhancement) */
  guidedContext?: {
    // User's RAW input - most important!
    originalCharacterConcept?: string;
    originalRole?: string;
    // World context
    genre?: string;
    tone?: string;
    themes?: string[];
    worldRules?: string[];
    keyLocations?: Array<{ name: string; description: string }>;
    conflict?: {
      centralConflict?: string;
      stakes?: string;
    };
    // Opening scene user input
    openingScene?: string;
    startingLocation?: string;
    // Other characters
    otherCharacters?: Array<{ name: string; roleInStory?: string; primaryDrive?: string }>;
    relationships?: Array<{ fromCharacter: string; toCharacter: string; surfaceRelationship: string }>;
  };
}

interface EnhancedFields {
  current: Partial<ScaffoldedCharacter>;
  enhanced: Partial<ScaffoldedCharacter>;
}

type CollapsibleSection = 'basic' | 'psychological' | 'cognitive' | 'behavioral' | 'personality' | 'goals' | 'voice';

// NOTE: SectionHeader, FieldInput, and ArrayFieldInput are declared at module
// scope (not inside EditCharacterModal) so they keep a stable identity across
// renders. When these were defined inline, every keystroke created a new
// component type; React would unmount and remount the input, destroying focus
// — hence the "focus jumps to Age after one letter" bug.

function SectionHeader({
  section,
  title,
  icon,
  expandedSections,
  onToggle,
}: {
  section: CollapsibleSection;
  title: string;
  icon: string;
  expandedSections: Set<CollapsibleSection>;
  onToggle: (section: CollapsibleSection) => void;
}) {
  const expanded = expandedSections.has(section);
  return (
    <button
      onClick={() => onToggle(section)}
      className="w-full flex items-center justify-between p-3 bg-dark-700 hover:bg-dark-600 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-2">
        <span>{icon}</span>
        <span className="font-medium text-dark-50">{title}</span>
      </div>
      <svg
        className={`w-5 h-5 text-dark-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
}

function FieldInput({
  label,
  field,
  placeholder,
  multiline = false,
  editedCharacter,
  enhancedFields,
  onUpdate,
  onAcceptEnhanced,
}: {
  label: string;
  field: keyof ScaffoldedCharacter;
  placeholder?: string;
  multiline?: boolean;
  editedCharacter: ScaffoldedCharacter;
  enhancedFields: EnhancedFields | null;
  onUpdate: (field: keyof ScaffoldedCharacter, value: unknown) => void;
  onAcceptEnhanced: (field: keyof ScaffoldedCharacter) => void;
}) {
  const value = editedCharacter[field];
  const stringValue = typeof value === 'string' ? value : '';
  const hasEnhanced = isMeaningfulEnhancement(enhancedFields?.enhanced[field]) &&
                      enhancedFields!.enhanced[field] !== enhancedFields!.current[field];

  return (
    <div className="space-y-1">
      <label className="block text-sm text-dark-300">{label}</label>
      {hasEnhanced ? (
        <EnhancedFieldComparison
          field={field}
          label={label}
          currentValue={stringValue}
          enhancedValue={enhancedFields!.enhanced[field] as string}
          onAccept={() => onAcceptEnhanced(field)}
          onReject={() => {}}
        />
      ) : multiline ? (
        <textarea
          value={stringValue}
          onChange={(e) => onUpdate(field, e.target.value)}
          placeholder={placeholder}
          className="narrative-input w-full px-3 py-2 text-sm min-h-[80px]"
        />
      ) : (
        <input
          type="text"
          value={stringValue}
          onChange={(e) => onUpdate(field, e.target.value)}
          placeholder={placeholder}
          className="narrative-input w-full px-3 py-2 text-sm"
        />
      )}
    </div>
  );
}

function ArrayFieldInput({
  label,
  field,
  placeholder,
  editedCharacter,
  enhancedFields,
  onUpdateArray,
  onAcceptEnhanced,
}: {
  label: string;
  field: keyof ScaffoldedCharacter;
  placeholder?: string;
  editedCharacter: ScaffoldedCharacter;
  enhancedFields: EnhancedFields | null;
  onUpdateArray: (field: keyof ScaffoldedCharacter, value: string) => void;
  onAcceptEnhanced: (field: keyof ScaffoldedCharacter) => void;
}) {
  const value = editedCharacter[field];
  const arrayValue = Array.isArray(value) ? value : [];
  const hasEnhanced = enhancedFields?.enhanced[field] !== undefined &&
                      JSON.stringify(enhancedFields.enhanced[field]) !== JSON.stringify(enhancedFields.current[field]);

  return (
    <div className="space-y-1">
      <label className="block text-sm text-dark-300">{label}</label>
      {hasEnhanced ? (
        <EnhancedFieldComparison
          field={field}
          label={label}
          currentValue={arrayValue.join(', ')}
          enhancedValue={(enhancedFields!.enhanced[field] as string[])?.join(', ') || ''}
          onAccept={() => onAcceptEnhanced(field)}
          onReject={() => {}}
          isArray
        />
      ) : (
        <textarea
          value={arrayValue.join(', ')}
          onChange={(e) => onUpdateArray(field, e.target.value)}
          placeholder={placeholder}
          className="narrative-input w-full px-3 py-2 text-sm min-h-[60px]"
        />
      )}
      <p className="text-xs text-dark-500">Separate items with commas</p>
    </div>
  );
}

export function EditCharacterModal({ character, isOpen, onClose, onSave, worldPrompt, settingDefinition, guidedContext }: EditCharacterModalProps) {
  const [editedCharacter, setEditedCharacter] = useState<ScaffoldedCharacter>(character);
  const [expandedSections, setExpandedSections] = useState<Set<CollapsibleSection>>(new Set(['basic']));
  const [enhancePrompt, setEnhancePrompt] = useState('');
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState('');
  const [enhancedFields, setEnhancedFields] = useState<EnhancedFields | null>(null);
  const [validationError, setValidationError] = useState('');

  // Reset state when character changes
  useEffect(() => {
    setEditedCharacter(character);
    setEnhancedFields(null);
    setEnhancePrompt('');
    setEnhanceError('');
    setValidationError('');
  }, [character]);

  if (!isOpen) return null;

  const toggleSection = (section: CollapsibleSection) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const update = (field: keyof ScaffoldedCharacter, value: unknown) => {
    setEditedCharacter(prev => ({ ...prev, [field]: value }));
    setValidationError('');
  };

  const updateArray = (field: keyof ScaffoldedCharacter, value: string) => {
    // Split by comma or newline and trim
    const items = value.split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    update(field, items);
  };

  const handleSave = () => {
    // Validate required field
    if (!editedCharacter.name.trim()) {
      setValidationError('Character name is required');
      setExpandedSections(prev => new Set([...prev, 'basic']));
      return;
    }
    onSave(editedCharacter);
    onClose();
  };

  const handleEnhance = async () => {
    if (!enhancePrompt.trim()) {
      setEnhanceError('Please describe the character first');
      return;
    }

    setIsEnhancing(true);
    setEnhanceError('');
    setEnhancedFields(null);

    try {
      const request = {
        description: enhancePrompt,
        worldPrompt,
        settingDefinition,
        existingData: editedCharacter,
        guidedContext,
      };
      
      const result = await enhanceCharacter(request);

      // Store both current and enhanced values for comparison
      setEnhancedFields({
        current: { ...editedCharacter },
        enhanced: result,
      });
    } catch (err) {
      setEnhanceError(err instanceof Error ? err.message : 'Enhancement failed');
    } finally {
      setIsEnhancing(false);
    }
  };

  const acceptEnhancedField = (field: keyof ScaffoldedCharacter) => {
    if (!enhancedFields) return;
    const value = enhancedFields.enhanced[field];
    if (isMeaningfulEnhancement(value)) {
      update(field, value);
    }
  };

  const acceptAllEnhanced = () => {
    if (!enhancedFields) return;
    // Only take fields the model actually filled in. Spreading the whole
    // enhanced object used to write nulls over existing good values, so one
    // bad enhancement could silently wipe a finished character.
    const accepted: Partial<ScaffoldedCharacter> = {};
    for (const [field, value] of Object.entries(enhancedFields.enhanced)) {
      if (isMeaningfulEnhancement(value)) {
        (accepted as Record<string, unknown>)[field] = value;
      }
    }
    setEditedCharacter(prev => ({
      ...prev,
      ...accepted,
      // Keep the name if enhanced name is empty
      name: (enhancedFields.enhanced.name?.trim() || prev.name),
    }));
    setEnhancedFields(null);
  };

  const dismissEnhanced = () => {
    setEnhancedFields(null);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
      <div className="bg-dark-800 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-dark-50">Edit Character</h2>
          <button onClick={onClose} className="text-dark-400 hover:text-dark-50">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {validationError && (
            <div className="bg-red-900/30 border border-red-700 text-red-200 px-4 py-2 rounded text-sm">
              {validationError}
            </div>
          )}

          {/* Enhanced Fields Banner */}
          {enhancedFields && (
            <div className="bg-primary-900/30 border border-primary-600 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-primary-400">✨</span>
                  <span className="text-primary-200 font-medium">Enhanced fields ready for review</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={acceptAllEnhanced}
                    className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={dismissEnhanced}
                    className="px-3 py-1 bg-dark-600 hover:bg-dark-500 text-dark-200 text-sm rounded"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              <p className="text-dark-300 text-sm">
                Review each field below. Fields with changes show "Current → Enhanced" comparison.
              </p>
            </div>
          )}

          {/* Basic Info Section */}
          <div>
            <SectionHeader expandedSections={expandedSections} onToggle={toggleSection} section="basic" title="Basic Information" icon="👤" />
            {expandedSections.has('basic') && (
              <div className="mt-2 p-4 bg-dark-700/50 rounded-lg space-y-4">
                <div className="space-y-1">
                  <label className="block text-sm text-dark-300">
                    Name <span className="text-red-400">*</span>
                  </label>
                  {enhancedFields?.enhanced.name && enhancedFields.enhanced.name !== enhancedFields.current.name ? (
                    <EnhancedFieldComparison
                      field="name"
                      label="Name"
                      currentValue={editedCharacter.name}
                      enhancedValue={enhancedFields.enhanced.name}
                      onAccept={() => acceptEnhancedField('name')}
                      onReject={() => {}}
                    />
                  ) : (
                    <input
                      type="text"
                      value={editedCharacter.name}
                      onChange={(e) => update('name', e.target.value)}
                      placeholder="Character name"
                      className={`narrative-input w-full px-3 py-2 text-sm ${!editedCharacter.name.trim() ? 'border-red-500' : ''}`}
                    />
                  )}
                </div>
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}label="Age" field="age" placeholder="e.g., 28, early 30s, ancient" />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}label="Physical Description" field="physicalDescription" placeholder="Describe their appearance..." multiline />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}label="Role in Story" field="roleInStory" placeholder="e.g., protagonist, mentor, antagonist" />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}label="Background" field="background" placeholder="Their history and backstory..." multiline />
              </div>
            )}
          </div>

          {/* Psychological Core Section */}
          <div>
            <SectionHeader expandedSections={expandedSections} onToggle={toggleSection} section="psychological" title="Psychological Core" icon="💜" />
            {expandedSections.has('psychological') && (
              <div className="mt-2 p-4 bg-dark-700/50 rounded-lg space-y-4">
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Primary Drive" 
                  field="primaryDrive" 
                  placeholder="The unconscious motivation beneath all their choices..."
                  multiline
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Core Wound" 
                  field="coreWound" 
                  placeholder="Formative experience that shaped who they are..."
                  multiline
                />
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Defense Mechanisms" 
                  field="defenseMechanisms" 
                  placeholder="denial, projection, humor as deflection..."
                />
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Blind Spots" 
                  field="blindSpots" 
                  placeholder="What they cannot see about themselves..."
                />
              </div>
            )}
          </div>

          {/* Cognitive Style Section */}
          <div>
            <SectionHeader expandedSections={expandedSections} onToggle={toggleSection} section="cognitive" title="Cognitive Style" icon="🧠" />
            {expandedSections.has('cognitive') && (
              <div className="mt-2 p-4 bg-dark-700/50 rounded-lg space-y-4">
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Intelligence Signature" 
                  field="intelligenceSignature" 
                  placeholder="HOW they think (analytical, intuitive, creative)..."
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Decision Architecture" 
                  field="decisionArchitecture" 
                  placeholder="What inputs they weight most when deciding..."
                  multiline
                />
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Knowledge Domains" 
                  field="knowledgeDomains" 
                  placeholder="Areas of expertise and notable gaps..."
                />
              </div>
            )}
          </div>

          {/* Behavioral Patterns Section */}
          <div>
            <SectionHeader expandedSections={expandedSections} onToggle={toggleSection} section="behavioral" title="Behavioral Patterns" icon="🎭" />
            {expandedSections.has('behavioral') && (
              <div className="mt-2 p-4 bg-dark-700/50 rounded-lg space-y-4">
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Observable Habits" 
                  field="observableHabits" 
                  placeholder="Physical behaviors revealing internal states..."
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Stress Response" 
                  field="stressResponse" 
                  placeholder="How they behave under pressure..."
                  multiline
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Social Calibration" 
                  field="socialCalibration" 
                  placeholder="How they adjust across different contexts..."
                  multiline
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Attachment Style" 
                  field="attachmentStyle" 
                  placeholder="How they form and maintain bonds..."
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Power Orientation" 
                  field="powerOrientation" 
                  placeholder="How they relate to authority..."
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Trust Calculus" 
                  field="trustCalculus" 
                  placeholder="What earns/breaks their trust..."
                  multiline
                />
              </div>
            )}
          </div>

          {/* Goals & Secrets Section */}
          <div>
            <SectionHeader expandedSections={expandedSections} onToggle={toggleSection} section="goals" title="Goals & Secrets" icon="🎯" />
            {expandedSections.has('goals') && (
              <div className="mt-2 p-4 bg-dark-700/50 rounded-lg space-y-4">
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Surface Goals" 
                  field="surfaceGoals" 
                  placeholder="What they say they want..."
                />
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Hidden Goals" 
                  field="hiddenGoals" 
                  placeholder="What they actually want but won't admit..."
                />
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Secrets" 
                  field="secrets" 
                  placeholder="Information they actively conceal..."
                />
                <ArrayFieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdateArray={updateArray} onAcceptEnhanced={acceptEnhancedField}
                  label="Active Concerns" 
                  field="activeConcerns" 
                  placeholder="What's occupying their mental bandwidth right now..."
                />
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Current Emotional State" 
                  field="currentEmotionalState" 
                  placeholder="Where they are emotionally RIGHT NOW..."
                  multiline
                />
              </div>
            )}
          </div>

          {/* Voice Section */}
          <div>
            <SectionHeader expandedSections={expandedSections} onToggle={toggleSection} section="voice" title="Voice & Speech" icon="💬" />
            {expandedSections.has('voice') && (
              <div className="mt-2 p-4 bg-dark-700/50 rounded-lg space-y-4">
                <FieldInput editedCharacter={editedCharacter} enhancedFields={enhancedFields} onUpdate={update} onAcceptEnhanced={acceptEnhancedField}
                  label="Speech Patterns" 
                  field="speechPatterns" 
                  placeholder="e.g., formal, uses slang, speaks in riddles, heavy accent..." 
                  multiline
                />
              </div>
            )}
          </div>

          {/* AI Enhancement Section */}
          <div className="border-t border-dark-600 pt-4 mt-6">
            <h4 className="text-sm font-medium text-dark-50 mb-2 flex items-center gap-2">
              <span className="text-primary-400">✨</span>
              Describe the Character (AI Enhancement)
            </h4>
            <p className="text-dark-400 text-xs mb-3">
              Describe who this character is in your own words. The AI will analyze your description and suggest field values.
            </p>
            <textarea
              value={enhancePrompt}
              onChange={(e) => setEnhancePrompt(e.target.value)}
              placeholder="e.g., A grizzled detective in his 50s who's seen too much. He's cynical but has a soft spot for underdogs. Speaks in short, clipped sentences. Secretly trying to atone for a case he botched years ago..."
              className="narrative-input w-full px-3 py-3 min-h-[100px] text-sm mb-3"
            />
            {enhanceError && (
              <div className="text-red-400 text-sm mb-2">{enhanceError}</div>
            )}
            <button
              onClick={handleEnhance}
              disabled={isEnhancing}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded text-sm disabled:opacity-50 flex items-center gap-2"
            >
              {isEnhancing ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Enhance with AI
                </>
              )}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-dark-600">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary">
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

// Component for showing Current → Enhanced comparison
function EnhancedFieldComparison({
  field,
  label,
  currentValue,
  enhancedValue,
  onAccept,
  onReject,
  isArray = false,
}: {
  field: keyof ScaffoldedCharacter;
  label: string;
  currentValue: string;
  enhancedValue: string;
  onAccept: () => void;
  onReject: () => void;
  isArray?: boolean;
}) {
  const hasChange = currentValue !== enhancedValue;

  if (!hasChange) {
    return (
      <div className="narrative-input px-3 py-2 text-sm text-dark-300">
        {currentValue || <span className="italic text-dark-500">Empty</span>}
      </div>
    );
  }

  return (
    <div className="bg-dark-600 rounded-lg overflow-hidden">
      {/* Current Value */}
      <div className="p-3 border-b border-dark-500">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-dark-400 uppercase">Current</span>
        </div>
        <div className="text-sm text-dark-300">
          {currentValue || <span className="italic text-dark-500">Empty</span>}
        </div>
      </div>

      {/* Arrow */}
      <div className="flex items-center justify-center py-1 bg-dark-700">
        <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>

      {/* Enhanced Value */}
      <div className="p-3 bg-primary-900/20">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-medium text-primary-400 uppercase">Enhanced</span>
          <span className="text-primary-400">✨</span>
        </div>
        <div className="text-sm text-dark-50">
          {enhancedValue || <span className="italic text-dark-400">Empty</span>}
        </div>
      </div>

      {/* Actions */}
      <div className="flex border-t border-dark-500">
        <button
          onClick={onAccept}
          className="flex-1 px-3 py-2 text-sm text-primary-400 hover:bg-primary-900/30 transition-colors flex items-center justify-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Accept
        </button>
        <div className="w-px bg-dark-500" />
        <button
          onClick={onReject}
          className="flex-1 px-3 py-2 text-sm text-dark-400 hover:bg-dark-500 transition-colors flex items-center justify-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Keep Current
        </button>
      </div>
    </div>
  );
}
